// Real product screenshots. Coordinates are percentages of the untouched crop.
// Capture notes and refresh procedure: public/docs/screenshots/README.md.
const step = (title, copy, pin, area) => ({ title, copy, pin, area });
const docsVisuals = {
  chat: {
    image: 'chat.jpg', width: 815, height: 480,
    title: 'Your first conversation starts here.',
    caption: 'The Chat screen has everything you need in one composer.',
    alt: 'Gab AI Chat with the Chat and Work switch above the message box, plus attachment, model, dictation, and Voice Mode controls.',
    steps: [
      step('Choose Chat or Work', 'Use Chat for a conversation. Choose Work when you want a project or a finished file.', [32, 8], [35, 2, 30, 10]),
      step('Tell Gab AI what you need', 'Write a question or describe your goal in the message box.', [3, 74], [3, 76, 94, 10]),
      step('Pick a model, or use Auto', 'Open the model name to choose one yourself. Auto chooses for your request.', [81, 88], [80, 86, 8, 8]),
    ],
  },
  work: {
    image: 'work.jpg', width: 810, height: 490,
    title: 'Give Work a clear destination.',
    caption: 'Select Work, then describe the result you want. This example asks for an editable guide and a PDF.',
    alt: 'Work selected in Gab AI with a sample windowsill herb guide brief, attachment button, Smart approval, Auto model, and Send button.',
    steps: [
      step('Switch to Work', 'Start a new conversation and select Work at the top.', [66, 7], [49, 2, 16, 9]),
      step('Describe the deliverable', 'Include the subject, what to cover, and the file formats you need.', [3, 72], [3, 74, 94, 11]),
      step('Add context, then send', 'Use + for reference files. Review the approval setting and send your brief.', [3, 92], [3, 86, 94, 10]),
    ],
  },
  models: {
    image: 'models.jpg', width: 559, height: 599,
    title: 'Find the right model for the task.',
    caption: 'Open the model name below your message to browse the picker. Available models can change.',
    alt: 'Choose a model dialog with search, Text category, Favorites, Compare, model rows, price indicators, and favorite stars.',
    steps: [
      step('Search by name', 'Type the model you want into Search models.', [4, 9], [3, 9, 94, 7]),
      step('Narrow the list', 'Choose a category, or open Favorites to see models you have starred.', [4, 23], [3, 16, 80, 8]),
      step('Choose or save a favorite', 'Select a model row to use it. The star saves it to Favorites.', [96, 30], [3, 25, 94, 7]),
    ],
  },
  'chat-tools': {
    image: 'chat-tools.jpg', width: 800, height: 267,
    title: 'A little more context goes a long way.',
    caption: 'Open + beside your message for files, bots, and web search. Shown here: the lower part of that menu.',
    alt: 'The composer plus menu showing Chat with a bot, Web search, Select from library, and Add photos & files beside the message box.',
    steps: [
      step('Search the web', 'Turn on Web search when your question needs current information.', [42, 27], [3, 17, 40, 19]),
      step('Reuse a saved file', 'Select from library adds something already in your Files.', [42, 48], [3, 38, 40, 19]),
      step('Attach something new', 'Choose Add photos & files to attach a reference from your device.', [42, 66], [3, 57, 40, 19]),
    ],
  },
  voice: {
    image: 'chat.jpg', width: 815, height: 480,
    title: 'Type it. Dictate it. Talk it through.',
    caption: 'Dictation and Voice Mode are beside the model selector in Chat.',
    alt: 'Gab AI message composer with a microphone for dictation and a black waveform button for Voice Mode at its right edge.',
    steps: [
      step('Dictate a message', 'The small microphone turns speech into text in the composer.', [89, 84], [88, 86, 3, 8]),
      step('Start Voice Mode', 'Choose the waveform button for a spoken conversation. Allow microphone access when prompted.', [94, 84], [92, 85, 5, 10]),
      step('Use Chat', 'Voice Mode starts from Chat. Availability depends on your device and plan.', [33, 8], [35, 2, 15, 10]),
    ],
  },
  tools: {
    image: 'tools.jpg', width: 1044, height: 635,
    title: 'Find a tool built for your next step.',
    caption: 'Open Tools in the sidebar. Browse by what you want to make or change.',
    alt: 'Gab AI Tools catalog showing Create and Edit sections with Video Generator, Image Generator, Logo Generator, Image Editor, Image Upscaler, and Object Remover.',
    steps: [
      step('Choose what to create', 'Open a generator for a guided form, options, and a result preview.', [35, 29], [35, 28, 31, 27]),
      step('Edit an existing image', 'Start with Image Editor, Image Upscaler, or Object Remover.', [2, 70], [1, 69, 32, 28]),
      step('Keep a tool close', 'Use its heart to save it, then open Saved at the top.', [79, 6], [79, 2, 12, 8]),
    ],
  },
  images: {
    image: 'image-generator.jpg', width: 1030, height: 475,
    title: 'Turn a description into an image.',
    caption: 'Tools → Image Generator gives you a dedicated space to compose an image. Review the cost below the form before generating.',
    alt: 'Image Generator with a sample tiny robot prompt, Options, GPT Image 2 model selection, and an empty Result preview.',
    steps: [
      step('Describe the scene', 'Include the subject, lighting, style, and composition you have in mind.', [2, 34], [2, 35, 45, 31]),
      step('Set options and model', 'Choose the frame shape in Options and the image model below it.', [2, 72], [2, 69, 45, 29]),
      step('Review the result', 'After generation, the image appears in Result. Inspect it before saving or editing.', [51, 31], [51, 30, 48, 67]),
    ],
  },
  videos: {
    image: 'video-generator.jpg', width: 512, height: 710,
    title: 'Give your video a sense of direction.',
    caption: 'Tools → Video Generator. Expand Options to shape the clip before you generate.',
    alt: 'Video Generator form containing a forest camera-movement prompt and expanded Style, Aspect Ratio, and Duration settings.',
    steps: [
      step('Describe motion', 'Say what the subject and camera should do, as well as how the scene should look.', [4, 24], [4, 25, 92, 19]),
      step('Choose the frame', 'Set an aspect ratio for where you will use the video.', [4, 74], [4, 74, 92, 8]),
      step('Set the duration', 'Choose an available clip length. Options depend on the selected model.', [4, 91], [4, 90, 92, 8]),
    ],
  },
  'video-preview': {
    image: 'video-result.jpg', width: 645, height: 625,
    title: 'Your prompt. A playable result.',
    caption: 'A real video made in Gab AI: a red teacup beside a sunny window. Open the result to watch, download, or continue editing.',
    alt: 'A Gab AI conversation with a cinematic red teacup prompt, the generated video thumbnail with a play button, and the response action toolbar.',
    steps: [
      step('Start with the scene', 'The prompt describes the subject, setting, light, and camera movement.', [22, 3], [21, 1, 78, 20]),
      step('Open the result', 'Choose the video thumbnail to watch it in the preview panel. Download and editing controls are there, too.', [3, 34], [1, 33, 57, 58]),
      step('Share the conversation', 'Use Share below the result when you want to share the conversation.', [8, 93], [6, 95, 4, 5]),
    ],
  },
  agents: {
    image: 'bot-builder.jpg', width: 765, height: 713,
    title: 'Build a bot around a useful role.',
    caption: 'Open Bots → Create a bot. The setup walks you from an idea to instructions you can review.',
    alt: 'Gab AI bot creation wizard with Name, Purpose, Instructions, Avatar, and Review steps, suggested roles, and a Name your bot composer.',
    steps: [
      step('Follow the setup', 'Name, purpose, instructions, avatar, and review each get their own step.', [4, 3], [2, 1, 96, 6]),
      step('Start with a role', 'Choose a suggestion such as Photography Coach, or write a name yourself.', [4, 34], [3, 32, 93, 25]),
      step('Make it your own', 'Type a name in the composer, then continue to define what your bot should do.', [4, 86], [2, 86, 96, 13]),
    ],
  },
  collections: {
    image: 'collections.jpg', width: 480, height: 499,
    title: 'Give a project a place of its own.',
    caption: 'Choose New collection in the sidebar to bring related chats, files, and instructions together.',
    alt: 'Create new collection dialog with Garden plans entered as the name, an icon chooser, a description of collections, and the Create collection button.',
    steps: [
      step('Name the collection', 'Use a project or topic name that will be easy to find later.', [4, 15], [3, 15, 94, 10]),
      step('Pick an icon', 'Choose a visual cue for your project from the icon grid.', [4, 32], [3, 31, 94, 22]),
      step('Create your space', 'Select Create collection, then add the related conversations and files.', [4, 88], [3, 88, 94, 10]),
    ],
  },
  maps: {
    image: 'maps.jpg', width: 1048, height: 663,
    title: 'From places on a map to a day out.',
    caption: 'An example search for parks and gardens in Austin, Texas. Place details and hours change over time.',
    alt: 'Gab AI Maps search results showing a map of Austin with numbered park markers, a selected Zilker Botanical Garden card, directions and website actions, and Plan an outing.',
    steps: [
      step('Explore the map', 'Select a numbered marker or place card to focus on a location.', [5, 28], [1, 21, 98, 39]),
      step('Check a place', 'Use Directions, Call, Website, or Maps below the selected place.', [3, 67], [1, 60, 98, 16]),
      step('Plan an outing', 'Turn the places you found into an itinerary with stops and timing.', [85, 12], [84, 12, 15, 8]),
    ],
  },
  files: {
    image: 'files.jpg', width: 1034, height: 685,
    title: 'A home for your references and results.',
    caption: 'Open Files from the sidebar. This is the library before your first upload.',
    alt: 'Empty Gab AI Files library with Upload files, search, source and sort menus, file-type tabs, and grid/list view controls.',
    steps: [
      step('Add your files', 'Select Upload files, or drop files into the library.', [87, 6], [86, 2, 13, 7]),
      step('Find the right file', 'Search by name and filter by Documents, Images, Audio, Video, or Code.', [3, 31], [1, 13, 98, 25]),
      step('Choose a view', 'Use the grid or list controls. Source and sort menus help narrow larger libraries.', [97, 22], [71, 23, 28, 6]),
    ],
  },
  memory: {
    image: 'memory.jpg', width: 823, height: 436,
    title: 'Help Gab AI remember what matters.',
    caption: 'Open Settings → Memory to review and manage what Gab AI remembers about you.',
    alt: 'Gab AI Memory settings with Add a Memory, the memory settings control, Memory overview, and example preferences in an empty memory library.',
    steps: [
      step('Add a memory', 'Save a useful preference or fact with Add a Memory.', [92, 4], [77, 4, 19, 10]),
      step('Write your overview', 'Use Memory overview for a short introduction in your own words.', [93, 25], [2, 22, 97, 26]),
      step('Review memory settings', 'Open the sliders control beside Add a Memory to review the available settings.', [74, 4], [72, 4, 5, 10]),
    ],
  },
  'import-chats': {
    image: 'import-chats.jpg', width: 785, height: 429,
    title: 'Bring a conversation with you.',
    caption: 'Open Settings → Import. Choose the service you are moving from and follow its export instructions.',
    alt: 'Gab AI Import settings showing ChatGPT and Claude export instructions and separate Import buttons for each service.',
    steps: [
      step('Choose your service', 'Each service has its own row and instructions.', [3, 29], [2, 27, 82, 13]),
      step('Prepare the export', 'Read the instructions below the service name and find its conversation export file.', [3, 43], [2, 41, 95, 21]),
      step('Select Import', 'Use the Import button on that service’s row and select the requested file.', [96, 29], [86, 30, 10, 9]),
    ],
  },
  'code-preview': {
    image: 'code-preview.jpg', width: 1328, height: 756,
    title: 'Open the file. Try it for yourself.',
    caption: 'The real HTML preview studio, showing an example project estimator. Games and other HTML apps use these same controls. The sandbox can limit browser storage.',
    alt: 'Gab AI HTML preview studio with Code and Preview tabs, Select and Highlight tools, device and zoom menus, download controls, and a working example project estimator.',
    steps: [
      step('Try the app or inspect its code', 'Switch between Preview and Code to check how your file looks and how it works.', [17, 7], [9, 1, 11, 6]),
      step('Check different screen sizes', 'Choose Desktop, Tablet, or Phone in the device menu. Use the zoom menu beside it to inspect details.', [71, 7], [67, 1, 14, 6]),
      step('Ask for a revision', 'Open Ask for changes to describe an improvement. The download arrow in the top toolbar saves your file.', [3, 92], [1, 94, 11, 5]),
    ],
  },
  'code-refine': {
    image: 'code-refine.jpg', width: 840, height: 199,
    title: 'Say what to change, and what to keep.',
    caption: 'The editing panel at the bottom of the HTML preview studio. Add to chat prepares a request for you to review before sending.',
    alt: 'HTML artifact editing panel with instructions to make totals easier to scan while preserving calculations, design suggestion buttons, and Add to chat.',
    steps: [
      step('Be specific', 'Describe the improvement and the parts that should stay the same.', [3, 24], [1, 26, 98, 35]),
      step('Use a starting point', 'Polish design, Make responsive, and Improve accessibility offer common revision directions.', [43, 72], [1, 63, 40, 19]),
      step('Review it in chat', 'Choose Add to chat, then check the prepared message and send it when it says what you want.', [96, 57], [85, 63, 14, 19]),
    ],
  },
  itinerary: {
    image: 'itinerary-card.jpg', width: 760, height: 444,
    title: 'A plan you can use as you go.',
    caption: 'Part of a real itinerary card for an Austin coffee outing. Times and venue details are example output; check current information before visiting.',
    alt: 'An interactive Austin coffee itinerary with duration, driving assumption, stop count, a timed first stop, and Open map and View source buttons.',
    steps: [
      step('Check the shape of the plan', 'Review the duration, travel mode, and number of stops before you set out.', [3, 27], [3, 26, 94, 18]),
      step('Follow each stop', 'Each entry brings together the time, activity, address, and travel to the next stop.', [3, 59], [3, 58, 94, 31]),
      step('Open the map or source', 'Use the buttons below a stop to find the place or check the linked reference.', [43, 94], [13, 90, 27, 7]),
    ],
  },
  bookmarks: {
    image: 'bookmarks.jpg', width: 796, height: 733,
    title: 'Keep a useful answer within reach.',
    caption: 'Open More → Bookmarks to return to saved messages. Here, a saved itinerary is expanded so its controls are available again.',
    alt: 'Gab AI Bookmarks with a search field, Newest first sorting menu, and an expanded saved Austin coffee itinerary with map and source buttons.',
    steps: [
      step('Find a saved answer', 'Search your bookmarks for a word or topic from the message.', [3, 9], [1, 11, 79, 6]),
      step('Choose the order', 'Use the sort menu to change how your saved messages are listed.', [96, 9], [82, 11, 17, 6]),
      step('Open the saved message', 'Select the message heading to expand it. Interactive card controls remain available inside.', [3, 21], [1, 19, 98, 7]),
    ],
  },
  'task-configure': {
    image: 'task-configure.jpg', width: 560, height: 734,
    title: 'Give a recurring task a clear job.',
    caption: 'Open More → Tasks → New task. This example is a weekly gardening checklist; the task is still a draft.',
    alt: 'Create Task form with Chat selected under Run using, an AI model picker, a Weekly garden checklist name, gardening instructions, and Continue.',
    steps: [
      step('Choose how it should run', 'Select Chat or Work. For Chat, choose the model that will write the response.', [4, 19], [5, 20, 90, 21]),
      step('Name it and explain the job', 'Use a recognizable name and instructions that make sense each time the task runs.', [4, 47], [5, 49, 90, 35]),
      step('Set the schedule next', 'Continue to choose when it runs. You will review the remaining settings before creating the task.', [4, 94], [3, 92, 94, 6]),
    ],
  },
  'task-schedule': {
    image: 'task-schedule.jpg', width: 560, height: 734,
    title: 'Choose a rhythm that works for you.',
    caption: 'The Schedule step follows task configuration. This example shows the Weekly options before choosing a day and time.',
    alt: 'Task schedule wizard with Once, Daily, Weekly, and Monthly choices, day and time menus, timezone information, and Back and Continue buttons.',
    steps: [
      step('Pick a frequency', 'Run once, or choose a daily, weekly, or monthly schedule.', [4, 32], [5, 33, 90, 11]),
      step('Choose the day and time', 'The fields change with the frequency. For Weekly, select a day of the week and a time.', [4, 51], [5, 52, 90, 7]),
      step('Check the time zone', 'Read the schedule summary and time zone before continuing to email settings and review.', [4, 63], [5, 61, 90, 9]),
    ],
  },
  music: {
    image: 'music-generator.jpg', width: 493, height: 713,
    title: 'Describe the sound you have in mind.',
    caption: 'Tools → Music Generator, with Options expanded. Model selection, cost, and Generate Music appear farther down the form.',
    alt: 'Music Generator form with an acoustic garden soundtrack prompt, an empty optional lyrics field, Genre and Mood menus, and a duration slider.',
    steps: [
      step('Set the scene', 'Describe the instruments, energy, style, and use for your track.', [4, 3], [3, 4, 92, 17]),
      step('Add lyrics, or leave them out', 'Enter your lyrics for a song, or leave this field blank for an instrumental.', [4, 25], [3, 27, 92, 17]),
      step('Shape the track', 'Expand Options to choose a genre, mood, and duration. Available settings depend on the model.', [4, 48], [3, 49, 92, 50]),
    ],
  },
  'data-export': {
    image: 'data-export.jpg', width: 752, height: 126,
    title: 'Take a copy of your account data.',
    caption: 'Find Download your data in Settings → General. The archive downloads to your device.',
    alt: 'Download your data section in General settings describing the ZIP archive contents, exclusions, and Download ZIP button.',
    steps: [
      step('Review what is included', 'Read the archive contents and exclusions. Conversation markdown files are included alongside the account records.', [3, 13], [1, 23, 79, 60]),
      step('Download the archive', 'Choose Download ZIP and wait for the export. Open the README inside the archive to understand its files.', [89, 20], [82, 34, 17, 26]),
    ],
  },
};

const workSteps = [
  { title: 'Describe', copy: 'State the goal, audience, constraints, and file format.' },
  { title: 'Follow', copy: 'Read progress and answer questions in the conversation.' },
  { title: 'Review', copy: 'Open the delivered file and check the result.' },
  { title: 'Refine', copy: 'Ask for a change, then export the version you want.' },
];

// Visuals are woven into the existing guide at meaningful section boundaries.
// Text stays owned by the shipped catalog / guide service, without a DB reseed.
const guideVisuals = {
  index: [['chat'], ['work', 'Meet Gab AI Work'], ['voice', 'Voice Mode — Talk to AI'], ['tools', 'Everything You Need']],
  quickstart: [['chat'], ['models', 'Understanding the Interface']],
  overview: [['chat'], ['tools', 'Platform Capabilities']],
  chat: [['chat'], ['models', 'Choosing AI Models'], ['chat-tools', 'Attachments & Files']],
  models: [['models'], ['images', 'Image Generation Models'], ['videos', 'Video Generation']],
  voice: [['voice']],
  tools: [['tools'], ['images', 'How to Use a Tool']],
  images: [['images'], ['chat-tools', 'Image Variations']],
  videos: [['videos'], ['video-preview', 'Managing Videos']],
  agents: [['agents']],
  collections: [['collections'], ['files', 'Files in Collections']],
  files: [['files'], ['chat-tools', 'Uploading Files']],
  'web-search': [['chat-tools']],
  memory: [['memory'], ['collections', 'Collection memory']],
  privacy: [['memory']],
  maps: [['maps'], ['itinerary', 'Plan an outing']],
  'interactive-cards': [['itinerary'], ['bookmarks', 'Bookmark or save an image']],
  bookmarks: [['bookmarks']],
  tasks: [['task-configure'], ['task-schedule', 'Scheduling Options']],
  music: [['music']],
  'data-export': [['data-export', 'From the App']],
  'import-chats': [['import-chats']],
  work: [['work'], ['code-preview', '3. Open the artifact'], ['files', '4. Review, then revise']],
  'work-prompts': [['work'], ['workflow', '3. Define checks and delivery']],
  'work-artifacts': [['files'], ['code-preview', '1. Open the delivered file'], ['code-refine', '2. Use the right kind of edit']],
  'work-files': [['files'], ['code-preview', 'Check the final file']],
  'work-images': [['work'], ['workflow', '2. Open the image studio']],
  'work-videos': [['work'], ['video-preview', '3. Generate clips from the approved look']],
  'work-documents': [['work'], ['workflow', '2. Open and review the document']],
  'work-spreadsheets': [['work'], ['workflow', '2. Open the spreadsheet artifact']],
  'work-presentations': [['work'], ['workflow', '2. Review the slide sequence']],
  'work-games': [['work'], ['code-preview', '2. Open the code artifact and play'], ['code-refine', '3. Point to a change']],
  'work-video-editor': [['work']],
  'work-image-editor': [['work']],
  editors: [['work'], ['files', 'Saving and Exporting']],
};

function addDocsVisuals(slug, blocks = []) {
  const placements = guideVisuals[slug];
  if (!placements) return blocks;
  const result = [...blocks];
  for (const [visual, section] of placements) {
    if (result.some(block => visual === 'workflow' ? block.type === 'product-workflow' : block.type === 'product-visual' && block.visual === visual)) continue;
    const block = visual === 'workflow'
      ? { type: 'product-workflow', steps: workSteps }
      : { type: 'product-visual', visual, priority: !section };
    // A missing section must not drop a screenshot on the floor. Append it
    // for older database-managed copies whose headings differ.
    const sectionIndex = section ? result.findIndex(item => item.title === section) : -1;
    const position = !section ? 0 : sectionIndex >= 0 ? sectionIndex : result.length;
    result.splice(position, 0, block);
  }
  return result;
}

module.exports = { docsVisuals, guideVisuals, addDocsVisuals, workSteps };
